// ==========================================================================
// Plyr HTML5 helpers
// ==========================================================================

import support from './support';
import { createElement, removeElement } from './utils/elements';
import { on, triggerEvent } from './utils/events';
import is from './utils/is';
import { silencePromise } from './utils/promise';
import { setAspectRatio } from './utils/style';
import { getDate } from './utils/time';
import browser from './utils/browser';

const html5 = {
  getSources() {
    if (!this.isHTML5) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('source'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');

      if (is.empty(type)) {
        return true;
      }

      return support.mime.call(this, type);
    });
  },

  // Get quality levels
  getQualityOptions() {
    // Whether we're forcing all options (e.g. for streaming)
    if (this.config.quality.forced) {
      return this.config.quality.options;
    }

    // Get sizes from <source> elements
    return html5.getSources
      .call(this)
      .map((source) => Number(source.getAttribute('size')))
      .filter(Boolean);
  },

  setup() {
    if (!this.isHTML5) {
      return;
    }

    const player = this;

    if (html5.watermarkEnabled.call(player)) {
      on.call(player, player.media, 'playing', (event) => {
        player.debug.log(event.type);
        html5.watermarkRender.call(player);
      });

      on.call(player, player.media, 'pause ended emptied abort error waiting', (event) => {
        player.debug.log(event.type);
        html5.watermarkRelease.call(player);
      });
    }

    // Set speed options from config
    player.options.speed = player.config.speed.options; //[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 4]

    // Set aspect ratio if fixed
    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }

    // Quality
    Object.defineProperty(player.media, 'quality', {
      get() {
        // Get sources
        const sources = html5.getSources.call(player);
        const source = sources.find((s) => s.getAttribute('src') === player.source);

        // Return size, if match is found
        return source && Number(source.getAttribute('size'));
      },
      set(input) {
        if (player.quality === input) {
          return;
        }

        // If we're using an an external handler...
        if (player.config.quality.forced && is.function(player.config.quality.onChange)) {
          player.config.quality.onChange(input);
        } else {
          // Get sources
          const sources = html5.getSources.call(player);
          // Get first match for requested size
          const source = sources.find((s) => Number(s.getAttribute('size')) === input);

          // No matching source found
          if (!source) {
            return;
          }

          player.elements.inputs.seek.disabled = true;
          // Get current state
          const { currentTime, paused, preload, readyState, playbackRate } = player.media;

          // Set new source
          player.media.src = source.getAttribute('src');

          // Prevent loading if preload="none" and the current source isn't loaded (#1044)
          if (preload !== 'none' || readyState) {
            // Restore time
            player.once('loadedmetadata', () => {
              player.elements.inputs.seek.disabled = false;
              player.speed = playbackRate;
              player.currentTime = currentTime;

              // Resume playing
              if (!paused) {
                silencePromise(player.play());
              }
            });

            // Load new source
            player.media.load();
          }
        }

        // Trigger change event
        triggerEvent.call(player, player.media, 'qualitychange', false, {
          quality: input,
        });
      },
    });
  },

  // Cancel current network requests
  // See https://github.com/sampotts/plyr/issues/174
  cancelRequests() {
    if (!this.isHTML5) {
      return;
    }

    // Remove child sources
    removeElement(html5.getSources.call(this));

    // Set blank video src attribute
    // This is to prevent a MEDIA_ERR_SRC_NOT_SUPPORTED error
    // Info: http://stackoverflow.com/questions/32231579/how-to-properly-dispose-of-an-html5-video-and-close-socket-or-connection
    this.media.setAttribute('src', this.config.blankVideo);

    // Load the new empty source
    // This will cancel existing requests
    // See https://github.com/sampotts/plyr/issues/174
    this.media.load();

    // Debugging
    this.debug.log('Cancelled network requests');
  },

  watermarkEnabled() {
    const player = this;
    return player.isVideo && player.config.starplayer.watermark.enabled;
  },

  watermarkRender() {
    const player = this;
    player.elements.watermark = createElement('div');
    player.elements.wrapper.appendChild(player.elements.watermark);

    let interval = player.config.starplayer.watermark.positionChangeInterval;
    const intervalIsValid = is.number(interval) && interval > 0;
    interval = intervalIsValid ? interval : 5;

    let displayDuration = player.config.starplayer.watermark.displayDuration;
    const displayDurationIsValid = is.number(displayDuration) && displayDuration < interval;
    displayDuration = displayDurationIsValid ? displayDuration : interval;

    if (player.stars.watermarkTimer) {
      clearInterval(player.stars.watermarkTimer);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }

    player.stars.watermarkTimer = setInterval(() => {
      if (!player.elements.watermark.parentNode) {
        if (!is.element(player.elements.watermark)) {
          player.elements.watermark = createElement('div');
        }
        player.elements.wrapper.appendChild(player.elements.watermark);
      }

      player.elements.watermark.innerHTML = player.config.starplayer.watermark.text;
      let style = player.elements.watermark.style;
      style.position = 'absolute';
      style.width = '100%';
      style.display = 'block';
      style.margin = '0';
      style.padding = '0';
      style.textTransform = 'uppercase';
      style.fontWeight = 'bold';
      
      if (browser.isAndroid || browser.isIos) {
        style.fontSize = '100%';
      } else {
        style.fontSize = '150%';
      }
      style.color = 'white';
      style.opacity = '0.4';
      style.filter = 'opacity(40%)';
      style.zIndex = '1';

      let textAlign = 'initial';
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          textAlign = 'left';
          break;
        case 1:
          textAlign = 'center';
          break;
        case 2:
          textAlign = 'right';
          break;
      }
      style.textAlign = textAlign;

      let top;
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          top = 0;
          break;
        case 1:
          top = 45;
          break;
        case 2:
          top = 90;
          player.elements.watermark.innerHTML = getDate(new Date());
          break;
      }
      style.top = `${top}%`;

      player.stars.watermarkDurationTimer = setTimeout(() => {
        style.display = 'none';
      }, displayDuration * 1000 - 100);
    }, interval * 1000);
  },

  watermarkRelease() {
    const player = this;
    if (player.elements.watermark) {
      player.elements.watermark.remove();
    }

    if (player.stars.watermarkTimer) {
      clearInterval(player.stars.watermarkTimer);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }
  },
};

export default html5;
